'use client'

import { useAuth } from '@clerk/nextjs'
import { useCallback } from 'react'
import { env } from '@/config/env'
import { AppError } from '@/lib/errors/appError'

/**
 * `useApiFetch`/`apiFetch` always JSON-parse the response body — there is no way to get a binary
 * `Blob` through them. Same "dedicated seam when fetch/apiFetch can't do it" reasoning as
 * `useFileUpload` (which needed upload-progress events `fetch` can't provide); this one needs a
 * binary body `apiFetch`'s JSON parsing can't provide.
 *
 * The raw Plivo recording URL is never exposed to the browser — this hits the backend's own
 * authenticated proxy (`GET /calls/:callId/recording`) and hands back a same-origin-safe object
 * URL. Callers must `URL.revokeObjectURL` it once done (the audio element unmounting).
 */
export function useCallRecordingAudio() {
  const { getToken } = useAuth()

  const fetchAudioUrl = useCallback(
    async (callId: string): Promise<string> => {
      const token = await getToken()
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/api/v1/calls/${callId}/recording`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw AppError.fromResponse(response.status, body)
      }
      const blob = await response.blob()
      return URL.createObjectURL(blob)
    },
    [getToken],
  )

  return { fetchAudioUrl }
}
